import type { Vec3 } from '@rcs/domain';
import type { SampledTrack } from '@rcs/track-geometry';
import {
  AxesHelper,
  BoxGeometry,
  BufferGeometry,
  BackSide,
  BufferAttribute,
  Color,
  FogExp2,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  DirectionalLight,
  GridHelper,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  ShadowMaterial,
  SphereGeometry,
  Vector3,
  WebGLRenderer,
} from 'three';
import {
  clampLook,
  DEFAULT_FOLLOW_DISTANCE,
  DEFAULT_FOLLOW_HEIGHT,
  followCameraPose,
  passengerCameraPose,
  PASSENGER_FOV,
  type FollowDirection,
  type LookAngles,
  type VehiclePose,
} from './camera-modes';
import {
  buildVehicleModel,
  eyeHeightOf,
  type VehicleModel,
  type VehicleMotion,
  type VehicleModelSpec,
} from './vehicle-model';
import { DEFAULT_ENVIRONMENT_LOOK, lightingFor, type EnvironmentLook } from './environment-look';
import { fitDistance, presetDirection, type CameraPreset } from './camera-presets';
import {
  dragPlaneNormal,
  markerRadius,
  pickNearest,
  pickRadius,
  rayPlaneIntersection,
  type DragPlane,
} from './edit-math';
import {
  buildDecorationMatrices,
  buildRailGeometry,
  buildTieMatrices,
  decorationStyle,
  tieCount,
  tieSegments,
  type DecorationKind,
} from './track-mesh';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

/** 描画品質（FR-SET-001）。 */
export type RenderQuality = 'low' | 'medium' | 'high';

/** 品質ごとの描画設定（FR-SET-001）。 */
export interface QualitySettings {
  /** 描画解像度（devicePixelRatio）の上限（NFR-PRF-004） */
  readonly maxPixelRatio: number;
  /** アンチエイリアス。WebGL コンテキストの作成時に決まるため、変えるには作り直す */
  readonly antialias: boolean;
  /** 影のマップの大きさ（ピクセル）。0 は影なし */
  readonly shadowMapSize: number;
}

/** 低・中・高の描画設定。 */
export const QUALITY_SETTINGS: Readonly<Record<RenderQuality, QualitySettings>> = {
  low: { maxPixelRatio: 1, antialias: false, shadowMapSize: 0 },
  medium: { maxPixelRatio: 1.5, antialias: true, shadowMapSize: 1024 },
  high: { maxPixelRatio: 2, antialias: true, shadowMapSize: 2048 },
};

/** 品質ごとの描画解像度（devicePixelRatio）の上限（NFR-PRF-004）。 */
export const MAX_PIXEL_RATIO: Readonly<Record<RenderQuality, number>> = {
  low: QUALITY_SETTINGS.low.maxPixelRatio,
  medium: QUALITY_SETTINGS.medium.maxPixelRatio,
  high: QUALITY_SETTINGS.high.maxPixelRatio,
};

/** 現在の描画設定（確認用）。 */
export interface RenderSettings {
  readonly quality: RenderQuality;
  /** 実際に使っている描画解像度の倍率 */
  readonly pixelRatio: number;
  /** 影を描画しているか */
  readonly shadows: boolean;
  /** アンチエイリアスが有効か（コンテキスト作成時の設定） */
  readonly antialias: boolean;
}

/** {@link createViewport} のオプション。 */
export interface ViewportOptions {
  /** 描画品質（既定 `medium`） */
  readonly quality?: RenderQuality;
  /** WebGL コンテキストの消失・復帰を通知する（NFR-PLT-004） */
  readonly onContextLostChange?: (lost: boolean) => void;
  /** カメラ操作（回転・ズーム・パン）のたびに、視点の状態を通知する（FR-CAM-004） */
  readonly onCameraChange?: (state: CameraState) => void;
}

/** カメラの種類。 */
export type CameraMode = 'overview' | 'follow' | 'passenger';

export type { DecorationKind };

/** 区間ごとの表示（添え字は区間番号 = `track.segment`）。 */
export interface TrackSections {
  /** レール・枕木の色（`0xrrggbb`）。省略した区間は既定の色 */
  readonly colors?: readonly (number | undefined)[] | undefined;
  /** 付ける設備（リフトのチェーン・ブレーキのフィン・駅のホームなど）。省略した区間は設備なし */
  readonly decorations?: readonly (DecorationKind | undefined)[] | undefined;
}

/** 3D 上に表示する制御点の目印。 */
export interface ControlPointMarker {
  readonly id: string;
  readonly position: Vec3;
  readonly selected: boolean;
  /** 選択していないときの色（`0xrrggbb`）。省略は既定の青。区間の種別（リフト・ブレーキなど）の表示に使う */
  readonly color?: number | undefined;
}

/** レールの既定の色（区間の指定がないとき）。 */
const DEFAULT_RAIL_COLOR = 0x4a5560;

/** 枕木の既定の色（区間の指定がないとき）。 */
const DEFAULT_TIE_COLOR = 0x7a5a3a;

/** 長押しとみなす時間（ミリ秒）。 */
const LONG_PRESS_MS = 500;
/** 長押しの間に許す指のずれ（画面上の px）。これを超えて動いたら、長押しではなくドラッグ。 */
const LONG_PRESS_SLOP_PX = 10;

/** 制御点の編集操作の通知先（FR-EDT-001）。 */
export interface ControlPointHandlers {
  /** 制御点が選択された（`undefined` は、何もない所をタップして選択を解除した） */
  onSelect(id: string | undefined): void;
  /** ドラッグを始めた */
  onMoveStart?(id: string): void;
  /** ドラッグ中に、制御点が新しい位置へ動いた。位置は、カメラに正対する平面の上 */
  onMove(id: string, position: Vec3): void;
  /** ドラッグを終えた */
  onMoveEnd(id: string): void;
  /** 動かさずに長押しした（メニューを出す契機）。位置は画面（ビューポート）の座標 */
  onLongPress?(id: string, clientX: number, clientY: number): void;
}

/** {@link Viewport.setCameraMode} のオプション。 */
export interface CameraModeOptions {
  /** 追従の位置（既定 `behind`） */
  readonly follow?: FollowDirection;
}

/** 俯瞰カメラの状態。注視点から見た球面座標で表す。 */
export interface CameraState {
  /** 注視点からカメラまでの距離（m） */
  readonly distance: number;
  /** 水平方向の角度（ラジアン）。+Z 側から見て 0、+X 側が正 */
  readonly azimuth: number;
  /** 鉛直上方からの角度（ラジアン）。0 で真上、π/2 で水平 */
  readonly polar: number;
  /** 注視点の位置（m）。全体俯瞰ではパンで動き、追従では車両の位置 */
  readonly target: Vec3;
  /** カメラの位置（m） */
  readonly position: Vec3;
  /** 乗客視点で見回している向き（それ以外では 0） */
  readonly look: LookAngles;
}

/** 描画資源の統計。 */
export interface RenderStats {
  /** GPU 上のジオメトリ数 */
  readonly geometries: number;
  /** GPU 上のテクスチャ数 */
  readonly textures: number;
}

/** 3D ビューポート。描画ループは React の再描画と独立して動く（ADR-0004）。 */
export interface Viewport {
  /** GPU に載っている資源の数。メッシュの差し替えで増え続けないことの確認に使う */
  getRenderStats(): RenderStats;
  /**
   * カメラをコース全体が収まる位置へ動かす（プリセットの向きで、注視点はコースの中心）。
   * コースがまだ表示されていないときは何もしない。
   */
  setCameraPreset(preset: CameraPreset): void;
  /**
   * カメラの種類を切り替える。
   *
   * - `overview`：全体俯瞰（回転・ズーム・パンの操作）。切り替え前の視点へ戻る
   * - `follow`：走行中の車両を一定距離で追う（FR-CAM-006）。操作はできない
   * - `passenger`：座席から前方を見る（FR-CAM-001）。ドラッグで見回せる。ダブルタップで正面へ戻す
   */
  setCameraMode(mode: CameraMode, options?: CameraModeOptions): void;
  /** 乗客視点の見回しを、正面へ戻す */
  resetLook(): void;
  /** 現在のカメラの種類を返す */
  getCameraMode(): CameraMode;
  /**
   * 編集の表示・操作を有効にする。有効なあいだ、制御点の目印を表示し、タップで選択、
   * ドラッグで移動できる（全体俯瞰のときに使う）。
   */
  setEditing(enabled: boolean): void;
  /** 表示する制御点の目印を設定する（選択中のものは大きく表示する） */
  setControlPoints(markers: readonly ControlPointMarker[]): void;
  /** 制御点をドラッグするときの移動面を選ぶ（次のドラッグから有効） */
  setDragPlane(plane: DragPlane): void;
  /** 制御点の編集操作の通知先を設定する */
  setControlPointHandlers(handlers: ControlPointHandlers | undefined): void;
  /** 制御点の目印の、画面上の位置（ブラウザのビューポート座標 [px]）。E2E テスト用 */
  getControlPointScreenPosition(id: string): { x: number; y: number } | undefined;
  /** 現在の俯瞰カメラの状態を返す */
  getCameraState(): CameraState;
  /** 現在の描画設定を返す */
  getRenderSettings(): RenderSettings;
  /**
   * 描画品質を変更する。解像度と影はすぐに切り替わる。
   * アンチエイリアスは作成時の設定のままなので、切り替えるにはビューポートを作り直す。
   */
  setQuality(quality: RenderQuality): void;
  /**
   * 背景（空・地面・光・霧）を切り替える。以前の背景の GPU リソースは解放する。
   * 省略（`undefined`）は、既定の背景（昼・格子の地面）。
   */
  setEnvironment(look: EnvironmentLook | undefined): void;
  /**
   * コースの線を表示し、カメラをコース全体に合わせる。
   * レール 2 本と枕木のメッシュを作り直し、以前のメッシュの GPU リソースを解放する。
   *
   * @param sections 区間（`track.segment`）ごとの表示。リフト・ブレーキなどの区間を見分けるために使う
   */
  setTrack(track: SampledTrack, sections?: TrackSections): void;
  /** 枕木に使っている色（重複なし、`0xrrggbb`）。テストで色分けを確かめるために使う */
  getTieColors(): number[];
  /** 表示している設備の個数（種類ごと）。テストで設備の表示を確かめるために使う */
  getDecorationCounts(): Partial<Record<DecorationKind, number>>;
  /**
   * 車両の位置・姿勢を更新して再描画を要求する。`undefined` で車両を隠す。
   * 配列のときは、先頭の車両から順に、1 両ずつの位置・姿勢（編成がカーブに沿って曲がる）。
   * 足りない車両は、最後の姿勢のままにする。カメラは、先頭の車両に付く。
   */
  setVehicle(pose: VehiclePose | readonly VehiclePose[] | undefined): void;
  /**
   * 車両の見た目（3D モデル・乗客）を切り替える。以前のモデルの GPU リソースは解放する。
   * 省略（`undefined`）は、標準のコースター 1 両。乗客視点の目の高さも、モデルに合わせて変わる。
   */
  setVehicleModel(spec: VehicleModelSpec | undefined): void;
  /**
   * 走行の状態（走った距離・自転車の人がこいでいるか）を伝え、動く部品（自転車の車輪・ペダル・こぐ脚）を
   * 動かして再描画を要求する。位置・姿勢は `setVehicle` で更新する。
   */
  setVehicleMotion(motion: VehicleMotion): void;
  /** 表示している車両の数と乗客の人数。テストで車両の切り替えを確かめるために使う */
  getVehicleInfo(): { readonly model: string; readonly cars: number; readonly riders: number };
  /** 次のアニメーションフレームで 1 回描画する（変化がないときは描画しない） */
  requestRender(): void;
  /** GPU リソースとイベントを解放し、canvas を取り除く */
  dispose(): void;
}

/** WebGL2 を初期化できない環境で投げられる。 */
export class ViewportUnsupportedError extends Error {
  constructor(options?: ErrorOptions) {
    super('WebGL2 を初期化できませんでした', options);
    this.name = 'ViewportUnsupportedError';
  }
}

/**
 * `container` の中に canvas を作り、3D ビューポートを初期化する。
 *
 * 現時点では動作確認用の最小シーン（グリッド・座標軸・照明）を表示する。
 * コースのメッシュは `setTrack`、車両は `setVehicle` で表示する。
 *
 * @throws {ViewportUnsupportedError} WebGL2 を初期化できない場合
 */
export function createViewport(
  container: HTMLElement,
  viewportOptions: ViewportOptions = {},
): Viewport {
  const options = viewportOptions;
  let quality: RenderQuality = options.quality ?? 'medium';
  const initialQuality = quality;

  const canvas = document.createElement('canvas');
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  container.appendChild(canvas);

  let renderer: WebGLRenderer;
  try {
    renderer = new WebGLRenderer({
      canvas,
      antialias: QUALITY_SETTINGS[quality].antialias,
      powerPreference: 'high-performance',
    });
  } catch (cause) {
    canvas.remove();
    throw new ViewportUnsupportedError({ cause });
  }

  const scene = new Scene();

  const camera = new PerspectiveCamera(60, 1, 0.1, 5000);
  camera.position.set(30, 25, 30);
  camera.lookAt(0, 0, 0);

  // 俯瞰カメラの操作（FR-CAM-004, 010, 011）。three の既定の割り当てが要求と一致する：
  // マウスは左ドラッグ回転・右ドラッグパン・ホイールズーム、
  // タッチは 1 本指ドラッグ回転・2 本指でピンチズームとパン。
  // ブラウザ標準のズーム・スクロールは、canvas 側の touch-action: none と preventDefault で抑える。
  const controls = new OrbitControls(camera, canvas);
  controls.minDistance = 2;
  controls.maxDistance = 4000;
  controls.maxPolarAngle = Math.PI * 0.495; // 地面の下へ回り込まない
  controls.screenSpacePanning = true;

  // 追従カメラの注視点（全体俯瞰では OrbitControls の注視点を使う）
  const followTarget = new Vector3();
  let isOverview = true;
  let cameraMode: CameraMode = 'overview';
  let look: LookAngles = { yaw: 0, pitch: 0 };
  const defaultFov = camera.fov;

  const cameraState = (): CameraState => {
    const target = isOverview ? controls.target : followTarget;
    const offset = camera.position.clone().sub(target);
    const distance = offset.length();
    return {
      distance,
      azimuth: Math.atan2(offset.x, offset.z),
      polar: distance > 0 ? Math.acos(Math.min(1, Math.max(-1, offset.y / distance))) : 0,
      target: [target.x, target.y, target.z],
      position: [camera.position.x, camera.position.y, camera.position.z],
      look: cameraMode === 'passenger' ? look : { yaw: 0, pitch: 0 },
    };
  };

  const grid = new GridHelper(200, 40, 0x4a5a6a, 0x9fb0c0);
  const axes = new AxesHelper(5);
  const hemisphere = new HemisphereLight(0xffffff, 0x5a6a50, 1.2);
  const sun = new DirectionalLight(0xffffff, 1.5);
  sun.position.set(50, 100, 30);
  scene.add(grid, axes, hemisphere, sun, sun.target);

  // 背景：空（頂点の色で上下に塗り分けた球。カメラに付いて動く）・地面（格子以外は色つきの平面）・霧
  const SKY_RADIUS = 2000;
  const skyGeometry = new SphereGeometry(SKY_RADIUS, 24, 16);
  const skyColors = new BufferAttribute(
    new Float32Array(skyGeometry.getAttribute('position').count * 3),
    3,
  );
  skyGeometry.setAttribute('color', skyColors);
  const skyMaterial = new MeshBasicMaterial({
    vertexColors: true,
    side: BackSide,
    fog: false,
    depthWrite: false,
  });
  const sky = new Mesh(skyGeometry, skyMaterial);
  sky.renderOrder = -1;
  sky.frustumCulled = false;
  scene.add(sky);
  const groundPlane = new Mesh(
    new PlaneGeometry(4000, 4000),
    new MeshStandardMaterial({ color: 0xb9c9d8, roughness: 1, metalness: 0 }),
  );
  groundPlane.rotation.x = -Math.PI / 2;
  groundPlane.position.y = -0.02;
  groundPlane.receiveShadow = true;
  groundPlane.visible = false;
  scene.add(groundPlane);
  let sunDirection = new Vector3(0.5, 1.6, 0.3).normalize();
  let sunDistance = 110;
  const placeSun = (center: Vector3): void => {
    sun.target.position.copy(center);
    sun.position.copy(center).addScaledVector(sunDirection, sunDistance);
  };
  const applyEnvironment = (look: EnvironmentLook): void => {
    const top = new Color(look.skyTop);
    const horizon = new Color(look.skyHorizon);
    const positions = skyGeometry.getAttribute('position');
    const mixed = new Color();
    for (let i = 0; i < positions.count; i += 1) {
      // 地平線（y = 0）が horizon、真上が top。地平線より下は horizon のまま
      const height = Math.max(0, positions.getY(i) / SKY_RADIUS);
      mixed.copy(horizon).lerp(top, Math.pow(height, 0.6));
      skyColors.setXYZ(i, mixed.r, mixed.g, mixed.b);
    }
    skyColors.needsUpdate = true;
    scene.background = horizon;
    // 霧の濃さ 1 でも、近くが見えなくならないように係数を抑える
    scene.fog = look.fogDensity > 0 ? new FogExp2(horizon.getHex(), look.fogDensity * 0.004) : null;
    const isGrid = look.ground === 'grid';
    grid.visible = isGrid;
    groundPlane.visible = !isGrid;
    groundPlane.material.color.set(look.groundColor);
    const light = lightingFor(
      look.timeOfDay,
      look.sunIntensity,
      look.sunAzimuth !== undefined && look.sunElevation !== undefined
        ? { azimuth: look.sunAzimuth, elevation: look.sunElevation }
        : undefined,
    );
    sunDirection = new Vector3(...light.sunDirection);
    sun.intensity = light.sunIntensity;
    sun.color.setHex(light.sunColor);
    hemisphere.intensity = light.ambientIntensity;
    // 夜は空からの光を青みに
    const day = Math.min(1, light.ambientIntensity / 1.2);
    hemisphere.color.setRGB(1, 1, 1).lerp(new Color(0x6070b0), 1 - day);
    placeSun(trackBounds?.center ?? new Vector3());
  };

  // 影：太陽の平行光が、レール・枕木・車両の影を地面とコースへ落とす。品質「低」では描かない
  renderer.shadowMap.type = PCFSoftShadowMap;
  const ground = new Mesh(
    new PlaneGeometry(4000, 4000),
    new ShadowMaterial({ opacity: 0.28, transparent: true }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0.02;
  ground.receiveShadow = true;
  scene.add(ground);
  const applyShadows = (): void => {
    const size = QUALITY_SETTINGS[quality].shadowMapSize;
    const enabled = size > 0;
    renderer.shadowMap.enabled = enabled;
    sun.castShadow = enabled;
    ground.visible = enabled;
    if (sun.shadow.mapSize.x !== size && enabled) {
      sun.shadow.map?.dispose();
      sun.shadow.map = null;
      sun.shadow.mapSize.set(size, size);
    }
    // 影の有無が変わると、材質の描画プログラムを作り直す必要がある
    scene.traverse((object) => {
      if (object instanceof Mesh) {
        const materials: unknown = object.material;
        for (const material of Array.isArray(materials) ? materials : [materials]) {
          if (material instanceof MeshStandardMaterial || material instanceof ShadowMaterial) {
            material.needsUpdate = true;
          }
        }
      }
    });
  };

  applyShadows();

  // 車両：車両（1 両ずつのグループ）の編成。前方は局所の −Z、上方は +Y、右方は +X。
  // 原点は車両の中心で、レールの高さ（モデルは `vehicle-model.ts`）
  const DEFAULT_VEHICLE_SPEC: VehicleModelSpec = {
    model: 'coaster',
    carCount: 1,
    carLength: 2.4,
    carSpacing: 0,
    width: 1.4,
    height: 1.0,
    seatsPerCar: 2,
    occupancy: 1,
    color: 0xd23c3c,
  };
  const vehicle = new Group();
  vehicle.visible = false;
  scene.add(vehicle);
  let vehicleModel: VehicleModel | undefined;
  // 最後に伝えられた走行の状態（モデルを作り直したときに、動く部品へ反映する）
  let lastMotion: VehicleMotion | undefined;
  let vehicleKind: string = DEFAULT_VEHICLE_SPEC.model;
  let eyeHeight = eyeHeightOf(DEFAULT_VEHICLE_SPEC.model);
  let carPoses: readonly VehiclePose[] = [];
  // 追従カメラの距離・高さ・注視点：車両の大きさに合わせる（長い編成・背の高い車両でも画面に収まる）
  let followFraming = {
    distance: DEFAULT_FOLLOW_DISTANCE,
    height: DEFAULT_FOLLOW_HEIGHT,
    targetHeight: 0.5,
  };
  const placeCars = (): void => {
    const cars = vehicleModel?.cars ?? [];
    cars.forEach((car, index) => {
      const pose = carPoses[index] ?? carPoses[carPoses.length - 1];
      if (!pose) return;
      const [rx, ry, rz] = pose.right;
      const [ux, uy, uz] = pose.up;
      const [tx, ty, tz] = pose.tangent;
      // 局所 X = 右、Y = 上、Z = 後方（-T）。右手系になる
      car.matrixAutoUpdate = false;
      car.matrix.makeBasis(
        new Vector3(rx, ry, rz),
        new Vector3(ux, uy, uz),
        new Vector3(-tx, -ty, -tz),
      );
      car.matrix.setPosition(pose.position[0], pose.position[1], pose.position[2]);
      car.matrixWorldNeedsUpdate = true;
      // 乗客視点では、カメラが先頭の車両の中にあるので、先頭の車両は描かない
      car.visible = !(cameraMode === 'passenger' && index === 0);
    });
  };
  const disposeVehicleModel = (): void => {
    if (!vehicleModel) return;
    for (const car of vehicleModel.cars) vehicle.remove(car);
    vehicleModel.dispose();
    vehicleModel = undefined;
  };
  const installVehicleModel = (spec: VehicleModelSpec): void => {
    disposeVehicleModel();
    vehicleModel = buildVehicleModel(spec);
    if (lastMotion) vehicleModel.setMotion(lastMotion);
    for (const car of vehicleModel.cars) vehicle.add(car);
    vehicleKind = spec.model;
    // 編成が標準のコースター（約 15 m）より長いぶんだけ、離れる（編成の後ろから見られるように）。背が高い車両は、高く・高いところを見る
    const length = spec.carCount * (spec.carLength + spec.carSpacing);
    followFraming = {
      distance: DEFAULT_FOLLOW_DISTANCE + Math.max(0, length - 16),
      height: Math.max(DEFAULT_FOLLOW_HEIGHT, spec.height * 1.6 + 2),
      targetHeight: Math.max(0.5, (spec.height - 1.2) / 2 + 0.5),
    };
    eyeHeight = eyeHeightOf(spec.model);
    placeCars();
  };
  installVehicleModel(DEFAULT_VEHICLE_SPEC);

  // コース：レール 2 本（メッシュ）と枕木（InstancedMesh）。サンプル軌道が変わるたびに作り直す
  const railMaterial = new MeshStandardMaterial({
    color: DEFAULT_RAIL_COLOR,
    metalness: 0.4,
    roughness: 0.5,
  });
  // 区間ごとの色（頂点の色）を使うレールの材質
  const tintedRailMaterial = new MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    metalness: 0.4,
    roughness: 0.5,
  });
  const tieMaterial = new MeshStandardMaterial({ color: 0xffffff, roughness: 0.9 });
  // 設備の材質（種類ごと）と、いま表示している設備のメッシュ
  const decorationMaterials = new Map<DecorationKind, MeshStandardMaterial>();
  let decorationMeshes: InstancedMesh[] = [];
  const tieGeometry = new BoxGeometry(1, 1, 1);
  let trackBounds: { center: Vector3; radius: number } | undefined;
  applyEnvironment(DEFAULT_ENVIRONMENT_LOOK);
  let trackGroup: Group | undefined;
  let trackGeometries: BufferGeometry[] = [];
  let tieMesh: InstancedMesh | undefined;
  const disposeTrack = (): void => {
    if (trackGroup) scene.remove(trackGroup);
    for (const geometry of trackGeometries) geometry.dispose();
    tieMesh?.dispose();
    for (const mesh of decorationMeshes) mesh.dispose();
    decorationMeshes = [];
    trackGroup = undefined;
    trackGeometries = [];
    tieMesh = undefined;
  };

  let frameRequest: number | undefined;
  let contextLost = false;

  const render = (): void => {
    frameRequest = undefined;
    if (contextLost) return;
    sky.position.copy(camera.position);
    renderer.render(scene, camera);
  };

  const requestRender = (): void => {
    frameRequest ??= requestAnimationFrame(render);
  };

  const resize = (): void => {
    const width = Math.max(1, container.clientWidth);
    const height = Math.max(1, container.clientHeight);
    renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, QUALITY_SETTINGS[quality].maxPixelRatio),
    );
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    requestRender();
  };

  // Three.js は contextlost で preventDefault し、restored で内部状態を再構築する。
  // ここでは状態を通知し、復帰後に再描画する。
  const handleContextLost = (): void => {
    contextLost = true;
    options.onContextLostChange?.(true);
  };
  const handleContextRestored = (): void => {
    contextLost = false;
    options.onContextLostChange?.(false);
    resize();
  };
  canvas.addEventListener('webglcontextlost', handleContextLost);
  canvas.addEventListener('webglcontextrestored', handleContextRestored);

  controls.addEventListener('change', () => {
    requestRender();
    updateMarkersIfAny();
    options.onCameraChange?.(cameraState());
  });

  // 目印の大きさは、カメラの距離に合わせて更新する（定義は編集の節）
  let updateMarkersIfAny: () => void = () => undefined;

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container);
  resize();

  // カメラの種類：全体俯瞰以外では、操作を止めて車両に追従させる
  let modeOptions: CameraModeOptions = {};
  let lastPose: VehiclePose | undefined;
  let savedOverview: { position: Vector3; target: Vector3 } | undefined;
  let lastModeNotice = 0;

  const applyModeCamera = (): void => {
    if (cameraMode === 'overview' || !lastPose) return;
    const view =
      cameraMode === 'passenger'
        ? passengerCameraPose(lastPose, look, eyeHeight)
        : followCameraPose(
            lastPose,
            modeOptions.follow ?? 'behind',
            followFraming.distance,
            followFraming.height,
            followFraming.targetHeight,
          );
    camera.position.set(...view.position);
    camera.up.set(...view.up);
    camera.lookAt(view.target[0], view.target[1], view.target[2]);
    followTarget.set(...view.target);
    requestRender();
    // 追従中は毎フレーム動くので、通知は 0.1 秒に 1 回に絞る
    const now = performance.now();
    if (now - lastModeNotice > 100) {
      lastModeNotice = now;
      options.onCameraChange?.(cameraState());
    }
  };

  const setMode = (mode: CameraMode, modeSettings: CameraModeOptions = {}): void => {
    if (mode === 'overview') {
      if (cameraMode !== 'overview' && savedOverview) {
        camera.position.copy(savedOverview.position);
        controls.target.copy(savedOverview.target);
        camera.up.set(0, 1, 0);
      }
      cameraMode = 'overview';
      isOverview = true;
      camera.fov = defaultFov;
      camera.updateProjectionMatrix();
      vehicle.visible = lastPose !== undefined;
      placeCars();
      controls.enabled = true;
      controls.update();
      requestRender();
      options.onCameraChange?.(cameraState());
      return;
    }
    if (cameraMode === 'overview') {
      savedOverview = { position: camera.position.clone(), target: controls.target.clone() };
    }
    cameraMode = mode;
    isOverview = false;
    modeOptions = modeSettings;
    controls.enabled = false;
    camera.fov = mode === 'passenger' ? PASSENGER_FOV : defaultFov;
    camera.updateProjectionMatrix();
    if (mode === 'passenger') look = { yaw: 0, pitch: 0 };
    vehicle.visible = lastPose !== undefined;
    placeCars();
    lastModeNotice = 0;
    applyModeCamera();
  };

  // 乗客視点の見回し：ドラッグで向きを変える（指・マウスに世界が付いてくる向き）。ダブルタップで正面へ戻す
  const LOOK_RADIANS_PER_PIXEL = 0.005;
  let lookPointer: { id: number; x: number; y: number; moved: number } | undefined;
  let lastTap: { time: number; x: number; y: number } | undefined;

  const resetLook = (): void => {
    look = { yaw: 0, pitch: 0 };
    lastModeNotice = 0;
    applyModeCamera();
  };

  const handleLookDown = (event: PointerEvent): void => {
    if (cameraMode !== 'passenger' || !event.isPrimary) return;
    lookPointer = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: 0 };
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      // 合成イベントなどでは、キャプチャできなくても見回しは動く
    }
  };
  const handleLookMove = (event: PointerEvent): void => {
    if (cameraMode !== 'passenger' || lookPointer?.id !== event.pointerId) return;
    const dx = event.clientX - lookPointer.x;
    const dy = event.clientY - lookPointer.y;
    lookPointer.x = event.clientX;
    lookPointer.y = event.clientY;
    lookPointer.moved += Math.abs(dx) + Math.abs(dy);
    look = clampLook({
      yaw: look.yaw - dx * LOOK_RADIANS_PER_PIXEL,
      pitch: look.pitch + dy * LOOK_RADIANS_PER_PIXEL,
    });
    lastModeNotice = 0;
    applyModeCamera();
  };
  const handleLookUp = (event: PointerEvent): void => {
    if (lookPointer?.id !== event.pointerId) return;
    const wasTap = lookPointer.moved < 8;
    lookPointer = undefined;
    if (cameraMode !== 'passenger' || !wasTap) return;
    const near =
      lastTap !== undefined &&
      Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) < 30 &&
      event.timeStamp - lastTap.time < 350;
    if (near) {
      lastTap = undefined;
      resetLook();
    } else {
      lastTap = { time: event.timeStamp, x: event.clientX, y: event.clientY };
    }
  };
  const handleLookCancel = (event: PointerEvent): void => {
    if (lookPointer?.id === event.pointerId) lookPointer = undefined;
  };
  canvas.addEventListener('pointerdown', handleLookDown);
  canvas.addEventListener('pointermove', handleLookMove);
  canvas.addEventListener('pointerup', handleLookUp);
  canvas.addEventListener('pointercancel', handleLookCancel);

  // 制御点の編集（FR-EDT-001）：目印の表示、タップで選択、ドラッグで移動
  let editing = false;
  let markers: readonly ControlPointMarker[] = [];
  let handlers: ControlPointHandlers | undefined;
  let dragPlane: DragPlane = 'free';
  const markerGeometry = new SphereGeometry(1, 16, 12);
  // 目印は、レールやコースに隠れないよう、深度を見ずに最後に描く
  const markerMaterial = new MeshBasicMaterial({
    depthTest: false,
    transparent: true,
    opacity: 0.95,
  });
  let markerMesh: InstancedMesh | undefined;
  const markerDummy = new Object3D();
  const markerColor = new Color();

  const updateMarkers = (): void => {
    if (!markerMesh) return;
    markers.forEach((marker, index) => {
      const distance = camera.position.distanceTo(markerDummy.position.set(...marker.position));
      markerDummy.scale.setScalar(markerRadius(distance, marker.selected));
      markerDummy.updateMatrix();
      markerMesh?.setMatrixAt(index, markerDummy.matrix);
      markerMesh?.setColorAt(
        index,
        markerColor.set(marker.selected ? 0xf0a500 : (marker.color ?? 0x1f6fd0)),
      );
    });
    markerMesh.instanceMatrix.needsUpdate = true;
    if (markerMesh.instanceColor) markerMesh.instanceColor.needsUpdate = true;
    markerMesh.visible = editing;
    requestRender();
  };

  updateMarkersIfAny = updateMarkers;

  const setMarkers = (next: readonly ControlPointMarker[]): void => {
    markers = next;
    if (markerMesh?.count !== next.length) {
      if (markerMesh) {
        scene.remove(markerMesh);
        markerMesh.dispose();
      }
      markerMesh = undefined;
      if (next.length > 0) {
        markerMesh = new InstancedMesh(markerGeometry, markerMaterial, next.length);
        markerMesh.renderOrder = 10;
        markerMesh.frustumCulled = false;
        scene.add(markerMesh);
      }
    }
    updateMarkers();
  };

  /** 画面上の位置（canvas 内のピクセル）に投影する。カメラの後ろにあれば `undefined`。 */
  const projectToCanvas = (position: Vec3): { x: number; y: number } | undefined => {
    const projected = new Vector3(...position).project(camera);
    if (projected.z > 1) return undefined;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (projected.x * 0.5 + 0.5) * rect.width,
      y: (-projected.y * 0.5 + 0.5) * rect.height,
    };
  };

  /** ポインターの位置から出る光線（原点・方向）。 */
  const rayAt = (clientX: number, clientY: number): { origin: Vec3; direction: Vec3 } => {
    const rect = canvas.getBoundingClientRect();
    const ndc = new Vector3(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
      0.5,
    ).unproject(camera);
    const direction = ndc.sub(camera.position).normalize();
    return {
      origin: [camera.position.x, camera.position.y, camera.position.z],
      direction: [direction.x, direction.y, direction.z],
    };
  };

  let drag:
    | {
        id: string;
        pointerId: number;
        planePoint: Vec3;
        normal: Vec3;
        offset: Vec3;
        startX: number;
        startY: number;
      }
    | undefined;
  let longPressTimer: ReturnType<typeof setTimeout> | undefined;
  const clearLongPress = (): void => {
    if (longPressTimer !== undefined) clearTimeout(longPressTimer);
    longPressTimer = undefined;
  };
  let emptyTap: { pointerId: number; x: number; y: number } | undefined;

  const handleEditDown = (event: PointerEvent): void => {
    if (!editing || !event.isPrimary || event.button !== 0 || event.target !== canvas) return;
    const rect = canvas.getBoundingClientRect();
    const candidates = markers.flatMap((marker) => {
      const screen = projectToCanvas(marker.position);
      return screen ? [{ id: marker.id, ...screen }] : [];
    });
    const id = pickNearest(
      candidates,
      event.clientX - rect.left,
      event.clientY - rect.top,
      pickRadius(event.pointerType),
    );
    const marker = markers.find((m) => m.id === id);
    if (id === undefined || !marker) {
      // 何もない所：動かさずに離したら、選択を解除する（動かしたときは、カメラの操作）
      emptyTap = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      return;
    }
    // 目印をつかんだ：カメラの操作（OrbitControls）に渡さない
    event.stopPropagation();
    event.preventDefault();
    const look = camera.getWorldDirection(new Vector3());
    const normal = dragPlaneNormal(dragPlane, [look.x, look.y, look.z]);
    const ray = rayAt(event.clientX, event.clientY);
    const hit = rayPlaneIntersection(ray.origin, ray.direction, marker.position, normal);
    const grabbed = hit ?? marker.position;
    drag = {
      id,
      pointerId: event.pointerId,
      planePoint: marker.position,
      normal,
      offset: [
        marker.position[0] - grabbed[0],
        marker.position[1] - grabbed[1],
        marker.position[2] - grabbed[2],
      ],
      startX: event.clientX,
      startY: event.clientY,
    };
    controls.enabled = false;
    handlers?.onSelect(id);
    handlers?.onMoveStart?.(id);
    // 動かさずに長押ししたら、ドラッグをやめてメニューの契機を通知する
    clearLongPress();
    const pointerId = event.pointerId;
    longPressTimer = setTimeout(() => {
      longPressTimer = undefined;
      if (drag?.pointerId !== pointerId) return;
      const { id: pressed, startX, startY } = drag;
      drag = undefined;
      controls.enabled = cameraMode === 'overview';
      handlers?.onMoveEnd(pressed);
      handlers?.onLongPress?.(pressed, startX, startY);
    }, LONG_PRESS_MS);
  };
  const handleEditMove = (event: PointerEvent): void => {
    if (drag?.pointerId !== event.pointerId) return;
    // 指のわずかなずれは長押しの範囲。それを超えて動いたら長押しではない
    if (
      longPressTimer !== undefined &&
      Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) > LONG_PRESS_SLOP_PX
    ) {
      clearLongPress();
    }
    const ray = rayAt(event.clientX, event.clientY);
    const hit = rayPlaneIntersection(ray.origin, ray.direction, drag.planePoint, drag.normal);
    if (!hit) return;
    handlers?.onMove(drag.id, [
      hit[0] + drag.offset[0],
      hit[1] + drag.offset[1],
      hit[2] + drag.offset[2],
    ]);
  };
  const handleEditUp = (event: PointerEvent): void => {
    if (drag?.pointerId === event.pointerId) {
      clearLongPress();
      const { id } = drag;
      drag = undefined;
      controls.enabled = cameraMode === 'overview';
      handlers?.onMoveEnd(id);
      return;
    }
    if (emptyTap?.pointerId === event.pointerId) {
      const moved = Math.hypot(event.clientX - emptyTap.x, event.clientY - emptyTap.y);
      emptyTap = undefined;
      if (moved < 6) handlers?.onSelect(undefined);
    }
  };
  const handleEditCancel = (event: PointerEvent): void => {
    if (drag?.pointerId === event.pointerId) {
      clearLongPress();
      const { id } = drag;
      drag = undefined;
      controls.enabled = cameraMode === 'overview';
      handlers?.onMoveEnd(id);
    }
    if (emptyTap?.pointerId === event.pointerId) emptyTap = undefined;
  };
  // 目印の選択は、カメラの操作（canvas 上の OrbitControls）より先に処理する（キャプチャ段階）
  container.addEventListener('pointerdown', handleEditDown, true);
  window.addEventListener('pointermove', handleEditMove);
  window.addEventListener('pointerup', handleEditUp);
  window.addEventListener('pointercancel', handleEditCancel);

  const applyPreset = (preset: CameraPreset): void => {
    if (!trackBounds) return;
    const { center, radius } = trackBounds;
    const direction = presetDirection(preset);
    const distance = fitDistance(radius, camera.fov, camera.aspect);
    camera.position.set(
      center.x + direction[0] * distance,
      center.y + direction[1] * distance,
      center.z + direction[2] * distance,
    );
    controls.target.copy(center);
    controls.maxDistance = Math.max(controls.maxDistance, distance * 2);
    camera.far = Math.max(camera.far, distance * 3);
    camera.updateProjectionMatrix();
    controls.update();
    requestRender();
  };

  return {
    setCameraPreset: applyPreset,
    setEditing(enabled) {
      editing = enabled;
      if (!enabled) {
        drag = undefined;
        emptyTap = undefined;
        controls.enabled = cameraMode === 'overview';
      }
      updateMarkers();
    },
    setControlPoints: setMarkers,
    getControlPointScreenPosition(id) {
      const marker = markers.find((m) => m.id === id);
      const screen = marker ? projectToCanvas(marker.position) : undefined;
      if (!screen) return undefined;
      const rect = canvas.getBoundingClientRect();
      return { x: rect.left + screen.x, y: rect.top + screen.y };
    },
    setDragPlane(plane) {
      dragPlane = plane;
    },
    setControlPointHandlers(next) {
      handlers = next;
    },
    setCameraMode: setMode,
    resetLook,
    getCameraMode: () => cameraMode,
    getCameraState: cameraState,
    getRenderStats: () => ({
      geometries: renderer.info.memory.geometries,
      textures: renderer.info.memory.textures,
    }),
    getRenderSettings: () => ({
      quality,
      pixelRatio: renderer.getPixelRatio(),
      shadows: renderer.shadowMap.enabled,
      antialias: QUALITY_SETTINGS[initialQuality].antialias,
    }),
    setQuality(next) {
      quality = next;
      applyShadows();
      resize();
    },
    setEnvironment(look) {
      applyEnvironment(look ?? DEFAULT_ENVIRONMENT_LOOK);
      requestRender();
    },
    setTrack(track, sections) {
      disposeTrack();
      trackGroup = new Group();
      for (const side of [-1, 1] as const) {
        const data = buildRailGeometry(
          track,
          side,
          {},
          sections?.colors === undefined
            ? undefined
            : { segmentColors: sections.colors, defaultColor: DEFAULT_RAIL_COLOR },
        );
        const geometry = new BufferGeometry();
        geometry.setAttribute('position', new BufferAttribute(data.positions, 3));
        if (data.colors) geometry.setAttribute('color', new BufferAttribute(data.colors, 3));
        geometry.setAttribute('normal', new BufferAttribute(data.normals, 3));
        geometry.setIndex(new BufferAttribute(data.indices, 1));
        geometry.computeBoundingSphere();
        trackGeometries.push(geometry);
        const rail = new Mesh(geometry, data.colors ? tintedRailMaterial : railMaterial);
        rail.castShadow = true;
        rail.receiveShadow = true;
        trackGroup.add(rail);
      }
      const ties = new InstancedMesh(tieGeometry, tieMaterial, tieCount(track));
      ties.instanceMatrix = new InstancedBufferAttribute(buildTieMatrices(track), 16);
      // 枕木の色は区間ごと（材質の色は白にして、枕木ごとの色をそのまま出す）
      const segmentOfTie = tieSegments(track);
      const tieColor = new Color();
      for (let i = 0; i < segmentOfTie.length; i += 1) {
        tieColor.setHex(sections?.colors?.[segmentOfTie[i] ?? 0] ?? DEFAULT_TIE_COLOR);
        ties.setColorAt(i, tieColor);
      }
      ties.computeBoundingSphere();
      ties.castShadow = true;
      ties.receiveShadow = true;
      tieMesh = ties;
      trackGroup.add(ties);
      // 設備：リフトのチェーン・ブレーキのフィン・発進加速の推進装置・駅のホーム
      if (sections?.decorations !== undefined) {
        for (const data of buildDecorationMatrices(track, sections.decorations)) {
          let material = decorationMaterials.get(data.kind);
          if (!material) {
            material = new MeshStandardMaterial({
              color: decorationStyle(data.kind).color,
              roughness: 0.6,
              metalness: 0.3,
            });
            decorationMaterials.set(data.kind, material);
          }
          const mesh = new InstancedMesh(tieGeometry, material, data.count);
          mesh.instanceMatrix = new InstancedBufferAttribute(data.matrices, 16);
          mesh.computeBoundingSphere();
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          decorationMeshes.push(mesh);
          trackGroup.add(mesh);
        }
      }
      scene.add(trackGroup);

      // カメラ合わせ用に、コースの範囲を求める（描画しない頂点は間引く）
      const stride = Math.max(1, Math.ceil(track.count / 4000));
      const min = new Vector3(Infinity, Infinity, Infinity);
      const max = new Vector3(-Infinity, -Infinity, -Infinity);
      const point = new Vector3();
      for (let k = 0; k < track.count; k += stride) {
        point.set(
          track.position[k * 3] ?? 0,
          track.position[k * 3 + 1] ?? 0,
          track.position[k * 3 + 2] ?? 0,
        );
        min.min(point);
        max.max(point);
      }
      // カメラをコース全体に合わせる
      const center = min.clone().add(max).multiplyScalar(0.5);
      const radius = Math.max(10, max.distanceTo(min) / 2);
      const isFirstTrack = trackBounds === undefined;
      trackBounds = { center: center.clone(), radius };
      // 影の範囲をコース全体に合わせる
      sunDistance = radius * 1.7;
      placeSun(center);
      sun.shadow.camera.left = -radius * 1.2;
      sun.shadow.camera.right = radius * 1.2;
      sun.shadow.camera.top = radius * 1.2;
      sun.shadow.camera.bottom = -radius * 1.2;
      sun.shadow.camera.near = 1;
      sun.shadow.camera.far = radius * 4;
      sun.shadow.camera.updateProjectionMatrix();
      sun.shadow.bias = -0.0005;
      controls.maxDistance = Math.max(4000, radius * 10);
      camera.far = Math.max(5000, radius * 10);
      // 初期の視点は、斜めのプリセット（コース全体が収まる位置）。編集で軌道が変わるたびに、視点を動かさない
      if (isFirstTrack) applyPreset('oblique');
    },
    getDecorationCounts() {
      const counts: Partial<Record<DecorationKind, number>> = {};
      for (const [kind, material] of decorationMaterials) {
        const mesh = decorationMeshes.find((m) => m.material === material);
        if (mesh) counts[kind] = mesh.count;
      }
      return counts;
    },
    getTieColors() {
      const colors = new Set<number>();
      const color = new Color();
      for (let i = 0; i < (tieMesh?.count ?? 0); i += 1) {
        tieMesh?.getColorAt(i, color);
        colors.add(color.getHex());
      }
      return [...colors].sort((a, b) => a - b);
    },
    setVehicleModel(spec) {
      installVehicleModel(spec ?? DEFAULT_VEHICLE_SPEC);
      requestRender();
    },
    setVehicleMotion(motion) {
      lastMotion = motion;
      vehicleModel?.setMotion(motion);
      requestRender();
    },
    getVehicleInfo() {
      return {
        model: vehicleKind,
        cars: vehicleModel?.cars.length ?? 0,
        riders: vehicleModel?.riders ?? 0,
      };
    },
    setVehicle(pose) {
      const poses: readonly VehiclePose[] =
        pose === undefined ? [] : 'position' in pose ? [pose] : pose;
      carPoses = poses;
      lastPose = poses[0];
      if (lastPose === undefined) {
        vehicle.visible = false;
      } else {
        vehicle.visible = true;
        placeCars();
        applyModeCamera();
      }
      requestRender();
    },
    requestRender,
    dispose() {
      if (frameRequest !== undefined) cancelAnimationFrame(frameRequest);
      resizeObserver.disconnect();
      controls.dispose();
      clearLongPress();
      container.removeEventListener('pointerdown', handleEditDown, true);
      window.removeEventListener('pointermove', handleEditMove);
      window.removeEventListener('pointerup', handleEditUp);
      window.removeEventListener('pointercancel', handleEditCancel);
      if (markerMesh) markerMesh.dispose();
      markerGeometry.dispose();
      markerMaterial.dispose();
      canvas.removeEventListener('pointerdown', handleLookDown);
      canvas.removeEventListener('pointermove', handleLookMove);
      canvas.removeEventListener('pointerup', handleLookUp);
      canvas.removeEventListener('pointercancel', handleLookCancel);
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored);
      grid.dispose();
      skyGeometry.dispose();
      skyMaterial.dispose();
      groundPlane.geometry.dispose();
      groundPlane.material.dispose();
      axes.dispose();
      disposeVehicleModel();
      disposeTrack();
      railMaterial.dispose();
      tintedRailMaterial.dispose();
      for (const material of decorationMaterials.values()) material.dispose();
      tieMaterial.dispose();
      tieGeometry.dispose();
      renderer.dispose();
      canvas.remove();
    },
  };
}
